import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Modal, Button, Divider } from 'antd';
import IconFont from '@/components/icon';
import './ImageCropModal.less';
import { useTranslation } from "react-i18next";

interface ImageCropModalProps {
    visible: boolean;
    onCancel: () => void;
    onConfirm: (croppedImageBlob: Blob) => void;
    imageFile: File | null;
}

interface Position {
    x: number;
    y: number;
}

interface CropArea {
    x: number;
    y: number;
    width: number;
    height: number;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
    visible,
    onCancel,
    onConfirm,
    imageFile
}) => {
    const { t } = useTranslation("chat");
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });
    const [fileSizeError, setFileSizeError] = useState(false);

    // 裁切区域固定为正方形，居中显示
    const cropSize = 200;
    const containerSize = 300;
    const cropArea: CropArea = {
        x: (containerSize - cropSize) / 2,
        y: (containerSize - cropSize) / 2,
        width: cropSize,
        height: cropSize
    };

    // 加载图片
    useEffect(() => {
        if (imageFile && visible) {
            // 检查文件大小 (4MB)
            const maxSize = 4 * 1024 * 1024;
            if (imageFile.size > maxSize) {
                setFileSizeError(true);
            } else {
                setFileSizeError(false);
            }
            
            const img = new Image();
            img.onload = () => {
                setImage(img);
                setImageLoaded(true);
                // 计算初始缩放比例，使图片适应裁切区域
                const scaleX = cropSize / img.width;
                const scaleY = cropSize / img.height;
                const initialScale = Math.max(scaleX, scaleY);
                setScale(initialScale);
                setPosition({ x: 0, y: 0 });
            };
            img.src = URL.createObjectURL(imageFile);
        }
    }, [imageFile, visible]);

    // 绘制画布
    const drawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');

        if (!canvas || !ctx || !image || !imageLoaded) return;

        // 清空画布
        ctx.clearRect(0, 0, containerSize, containerSize);

        // 计算图片绘制位置和尺寸
        const scaledWidth = image.width * scale;
        const scaledHeight = image.height * scale;
        const drawX = (containerSize - scaledWidth) / 2 + position.x;
        const drawY = (containerSize - scaledHeight) / 2 + position.y;

        // 绘制图片
        ctx.drawImage(image, drawX, drawY, scaledWidth, scaledHeight);

        // 绘制遮罩层（裁切区域外的半透明覆盖）
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';

        // 上方遮罩
        ctx.fillRect(0, 0, containerSize, cropArea.y);
        // 下方遮罩
        ctx.fillRect(0, cropArea.y + cropArea.height, containerSize, containerSize - cropArea.y - cropArea.height);
        // 左侧遮罩
        ctx.fillRect(0, cropArea.y, cropArea.x, cropArea.height);
        // 右侧遮罩
        ctx.fillRect(cropArea.x + cropArea.width, cropArea.y, containerSize - cropArea.x - cropArea.width, cropArea.height);

        // 绘制裁切框边框
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.strokeRect(cropArea.x, cropArea.y, cropArea.width, cropArea.height);

        // 绘制裁切框内的网格线
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        // 垂直线
        ctx.beginPath();
        ctx.moveTo(cropArea.x + cropArea.width / 3, cropArea.y);
        ctx.lineTo(cropArea.x + cropArea.width / 3, cropArea.y + cropArea.height);
        ctx.moveTo(cropArea.x + (cropArea.width * 2) / 3, cropArea.y);
        ctx.lineTo(cropArea.x + (cropArea.width * 2) / 3, cropArea.y + cropArea.height);
        ctx.stroke();
        // 水平线
        ctx.beginPath();
        ctx.moveTo(cropArea.x, cropArea.y + cropArea.height / 3);
        ctx.lineTo(cropArea.x + cropArea.width, cropArea.y + cropArea.height / 3);
        ctx.moveTo(cropArea.x, cropArea.y + (cropArea.height * 2) / 3);
        ctx.lineTo(cropArea.x + cropArea.width, cropArea.y + (cropArea.height * 2) / 3);
        ctx.stroke();
    }, [image, imageLoaded, scale, position]);

    // 重绘画布
    useEffect(() => {
        drawCanvas();
    }, [drawCanvas]);

    // 缩放控制
    const handleZoomIn = useCallback(() => {
        setScale(prev => Math.min(prev * 1.2, 5));
    }, []);

    const handleZoomOut = useCallback(() => {
        setScale(prev => Math.max(prev / 1.2, 0.1));
    }, []);

    // 鼠标拖拽开始
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    }, [position]);

    // 鼠标拖拽移动
    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isDragging) return;

        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        setPosition({ x: newX, y: newY });
    }, [isDragging, dragStart]);

    // 鼠标拖拽结束
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // 滚轮缩放
    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setScale(prev => Math.max(0.1, Math.min(5, prev * delta)));
    }, []);

    // 生成裁切后的图片
    const handleConfirm = useCallback(() => {
        if (!image || !imageLoaded) return;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        // 设置输出画布尺寸
        canvas.width = cropSize;
        canvas.height = cropSize;

        // 1. 选择背景色（示例：白色，可替换为其他颜色，如 #f0f0f0、rgba(255,255,255,0.8) 等）
        ctx.fillStyle = '#ffffff'; // 白色背景，可自定义
        // 2. 填充整个canvas区域（从坐标0,0到canvas的宽高）
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // 计算裁切区域在原图中的位置
        const scaledWidth = image.width * scale;
        const scaledHeight = image.height * scale;
        const imageX = (containerSize - scaledWidth) / 2 + position.x;
        const imageY = (containerSize - scaledHeight) / 2 + position.y;

        // 计算裁切区域相对于图片的位置
        const cropX = (cropArea.x - imageX) / scale;
        const cropY = (cropArea.y - imageY) / scale;
        const cropWidth = cropArea.width / scale;
        const cropHeight = cropArea.height / scale;

        // 绘制裁切后的图片
        ctx.drawImage(
            image,
            cropX, cropY, cropWidth, cropHeight,
            0, 0, cropSize, cropSize
        );

        // 转换为Blob
        canvas.toBlob((blob) => {
            if (blob) {
                onConfirm(blob);
            }
        }, 'image/jpeg', 0.9);
    }, [image, imageLoaded, scale, position, onConfirm]);

    const handleCancel = useCallback(() => {
        setImageLoaded(false);
        setImage(null);
        setScale(1);
        setPosition({ x: 0, y: 0 });
        setFileSizeError(false);
        onCancel();
    }, [onCancel]);

    // 重新上传功能
    const handleReupload = useCallback(() => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/jpeg,image/png,image/bmp,image/gif");
        input.onchange = (ev: any) => {
            if (ev.target.files && ev.target.files[0]) {
                const file = ev.target.files[0];
                
                // 验证文件类型
                const validTypes = ['image/jpeg', 'image/png', 'image/bmp', 'image/gif'];
                if (!validTypes.includes(file.type)) {
                    return;
                }
                
                // 验证文件大小 (4MB)
                const maxSize = 4 * 1024 * 1024;
                if (file.size > maxSize) {
                    setFileSizeError(true);
                    // return;
                } else {
                    setFileSizeError(false);
                }
                
                // 重置状态并加载新图片
                setImageLoaded(false);
                setImage(null);
                setScale(1);
                setPosition({ x: 0, y: 0 });
                
                // 加载新图片
                const img = new Image();
                img.onload = () => {
                    setImage(img);
                    setImageLoaded(true);
                    const scaleX = cropSize / img.width;
                    const scaleY = cropSize / img.height;
                    const initialScale = Math.max(scaleX, scaleY);
                    setScale(initialScale);
                    setPosition({ x: 0, y: 0 });
                };
                img.src = URL.createObjectURL(file);
            }
        };
        input.click();
    }, []);

    const modalFooterView = (
        <div>
            <Button
                style={{
                    border: "1px solid #dee0e3",
                    borderRadius: " 4px",
                    color: "#1B1B1B"
                }}
                onClick={handleCancel}
                className="cancelBtn"
            >
                {t("cancelText")}
            </Button>
            <Button
                type="primary"
                disabled={fileSizeError}
                style={{
                    border: "none",
                    background: "@primary-color",
                    borderRadius: "4px",
                    fontSize: "14px",
                    color: "#ffffff"
                }}
                onClick={handleConfirm}
            >
                {t("determine")}
            </Button>
        </div>
    );

    return (
        <Modal
            title="上传头像"
            visible={visible}
            onCancel={handleCancel}
            width={560}
            centered
            maskClosable={false}
            wrapClassName="image-crop-modal"
            footer={modalFooterView}
        >
            <div className="crop-modal-content">
                <div className="crop-container" ref={containerRef}>
                    <canvas
                        ref={canvasRef}
                        width={containerSize}
                        height={containerSize}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onWheel={handleWheel}
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                    />
                </div>
                {fileSizeError && (
                    <div className="crop-tips">
                        请上传4MB大小内的jpg、png文件
                    </div>
                )}
                <div onClick={handleReupload} className="crop-reupload">
                    重新上传
                </div>
                <div className="crop-controls">
                    <div className="zoom-controls">
                        <div className='zoom-icon' onClick={handleZoomIn}>
                            <IconFont type='icona-vector1' />
                        </div>
                        <Divider style={{height: "20px"}} type="vertical" />
                        <div className='zoom-icon' onClick={handleZoomOut}>
                            <IconFont type='icona-vector2' />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ImageCropModal;